import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import SupportClient, { CreateSupportRequest, SupportRequestType } from '../apiClients/SupportClient';
import CreateSupportRequestDialog from '../components/support/CreateSupportRequestDialog';
import { useAccessInfo } from '../contexts/AccessTokenProvider';
import { Securables } from '../Enums';
import {
  getContextHelpArticles,
  getHelpArticle,
  getVisibleHelpArticles,
  helpRoutes,
  resolveHelpRoute,
  searchHelpArticles,
} from '../help/catalog';
import HelpArticleBody from '../help/HelpArticleBody';
import HelpArticleCard from '../help/HelpArticleCard';
import { HelpArticle } from '../help/types';
import useViewportMode from '../hooks/useViewportMode';

const HelpCenterPage = () => {
  const history = useHistory();
  const location = useLocation();
  const locationQuery = new URLSearchParams(location.search);
  const slug = locationQuery.get('article') || undefined;
  const { hasRole } = useAccessInfo();
  const { mode, isPhone, isTablet, isPortrait } = useViewportMode();
  const hasCapability = (capability: string) => hasRole(capability as Securables, capability === Securables.synergist_admin);
  const visibleArticles = getVisibleHelpArticles(hasCapability);
  const routeQuery = locationQuery.get('route');
  const contextRoute = helpRoutes.find(route => route.id === routeQuery) || resolveHelpRoute(location.pathname);
  const [query, setQuery] = useState('');
  const [requestType, setRequestType] = useState<SupportRequestType>('Issue');
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestBusy, setRequestBusy] = useState(false);
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const [requestError, setRequestError] = useState('');

  const article = slug ? getHelpArticle(slug, hasCapability) : undefined;

  useEffect(() => {
    if (!article || !location.hash) return;
    const anchor = location.hash.slice(1);
    requestAnimationFrame(() => document.getElementById(anchor)?.focus());
  }, [article, location.hash]);

  const openArticle = (item: HelpArticle) => history.push(`/Help?article=${encodeURIComponent(item.slug)}`);
  const results = searchHelpArticles(visibleArticles, query, contextRoute.id);
  const contextArticles = getContextHelpArticles(visibleArticles, contextRoute.id);
  const contextualIds = new Set(contextArticles.map(item => item.id));
  const browseResults = query ? results : results.filter(item => !contextualIds.has(item.id));
  const categories = [...new Set(browseResults.map(item => item.category))];

  const submitRequest = async (request: CreateSupportRequest) => {
    try {
      setRequestBusy(true);
      setRequestError('');
      await new SupportClient().CreateRequest(request);
      setRequestOpen(false);
      setRequestSubmitted(true);
    } catch {
      setRequestError('The request could not be submitted. Your draft is still open so you can try again.');
    } finally {
      setRequestBusy(false);
    }
  };

  const startRequest = (type: SupportRequestType) => {
    setRequestType(type);
    setRequestOpen(true);
    setRequestError('');
  };

  if (slug) return <Paper
    component="article"
    data-testid="help-article-page"
    data-viewport-mode={mode}
    sx={{
      maxWidth: 1000,
      mx: 'auto',
      my: { xs: 0, sm: 2 },
      p: { xs: 2, sm: 3, md: 5 },
      flex: '1 1 auto',
      minHeight: 0,
      overflowY: 'auto',
      overscrollBehavior: 'contain',
      WebkitOverflowScrolling: 'touch',
    }}>
    <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => history.push('/Help')} sx={{ minHeight: 44, mb: 2 }}>Help Center</Button>
    {!article ? <Alert severity="warning">This article is unavailable or is not included in your current permissions.</Alert> : <>
      <Stack spacing={1.5} sx={{ mb: 4 }}>
        <Chip size="small" color="primary" variant="outlined" label={article.category} sx={{ alignSelf: 'flex-start' }} />
        <Typography component="h1" variant={isPhone ? 'h3' : 'h2'}>{article.title}</Typography>
        <Typography color="text.secondary" variant="h6" fontWeight={400}>{article.summary}</Typography>
        <Typography variant="caption" color="text.secondary">Version {article.version} · Maintained by {article.owner} · Review by {article.reviewBy}</Typography>
      </Stack>
      <Divider sx={{ mb: 4 }} />
      <HelpArticleBody article={article} />
    </>}
  </Paper>;

  return <Paper
    data-testid="help-center-page"
    data-viewport-mode={mode}
    sx={{
      maxWidth: 1500,
      mx: 'auto',
      my: { xs: 0, sm: 2 },
      flex: '1 1 auto',
      minHeight: 0,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    }}>
    <Box sx={{ flexShrink: 0, p: { xs: 2, sm: 3, md: 5 }, background: theme => `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.background.paper})` }}>
      <Typography component="h1" variant={isPhone ? 'h3' : 'h2'}>Help Center</Typography>
      <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 720 }}>Find practical guidance for the work you are doing in Synergist Pro.</Typography>
      <TextField
        fullWidth
        value={query}
        onChange={event => setQuery(event.target.value)}
        placeholder="Search positions, development templates, coaching, billing, and support"
        inputProps={{ 'aria-label': 'Search Help Center' }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon /></InputAdornment> }}
        sx={{ mt: 3, maxWidth: 820, bgcolor: 'background.paper', borderRadius: 1 }}
      />
    </Box>
    <Box
      data-testid="help-center-scroll-region"
      sx={{
        flex: '1 1 auto',
        minHeight: 0,
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
        p: { xs: 2, sm: 3, md: 4 },
      }}>
      {!query && contextArticles.length > 0 && <Box component="section" aria-labelledby="context-help-heading" sx={{ mb: 4 }}>
        <Typography id="context-help-heading" variant="h4">For {contextRoute.label}</Typography>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {contextArticles.map(item => <Grid item xs={12} md={isTablet && isPortrait ? 12 : 6} lg={4} key={item.id}><HelpArticleCard article={item} onOpen={openArticle} /></Grid>)}
        </Grid>
      </Box>}

      <Box component="section" aria-labelledby="all-help-heading">
        <Typography id="all-help-heading" variant="h4">{query ? `${results.length} search result${results.length === 1 ? '' : 's'}` : 'Browse all guidance'}</Typography>
        {browseResults.length === 0 && <Alert severity="info" sx={{ mt: 2 }}>No articles match that search. Try a shorter phrase, or send support a request below.</Alert>}
        {categories.map(category => <Box key={category} sx={{ mt: 3 }}>
          <Typography variant="overline" color="text.secondary">{category}</Typography>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            {browseResults.filter(item => item.category === category).map(item => <Grid item xs={12} md={isTablet && isPortrait ? 12 : 6} lg={4} key={item.id}><HelpArticleCard article={item} onOpen={openArticle} /></Grid>)}
          </Grid>
        </Box>)}
      </Box>

      <Card variant="outlined" sx={{ mt: 5 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} gap={2} alignItems={{ md: 'center' }} justifyContent="space-between">
            <Box><Typography variant="h5">Still need help?</Typography><Typography color="text.secondary">Support receives the current product area, never the contents of this page.</Typography></Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
              <Button startIcon={<BugReportOutlinedIcon />} onClick={() => startRequest('Issue')} sx={{ minHeight: 44 }}>Report an issue</Button>
              <Button startIcon={<LightbulbOutlinedIcon />} onClick={() => startRequest('FeatureRequest')} sx={{ minHeight: 44 }}>Request a feature</Button>
              <Button variant="contained" startIcon={<ConfirmationNumberOutlinedIcon />} onClick={() => history.push('/ClientRequests')} sx={{ minHeight: 44 }}>View requests</Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
    <CreateSupportRequestDialog open={requestOpen} initialType={requestType} routeId={contextRoute.id} busy={requestBusy} error={requestError} onClose={() => setRequestOpen(false)} onSubmit={submitRequest} />
    <Snackbar open={requestSubmitted} autoHideDuration={5000} onClose={() => setRequestSubmitted(false)} message="Support request submitted" />
  </Paper>;
};

export default HelpCenterPage;
